import {notNull} from '../../../../../../library/clxProductNet/assets/js/modules/common/assert';
import {MicroConversionTrackingEventsEnum} from '../../common/trackingMicroconversion/Model/EventProductCustomizationEnum';
import ProductCustomizationConfigModel from '../Model/productCustomizationConfigModel';
import ProductCustomizationStitchingConfigModel from '../Model/productCustomizationStitchingConfigModel';
import CustomizationPricingOverviewComponent from './customizationPricingOverviewComponent';
import FinishingColorsComponent from './finishingColorsComponent';
import UploadEmbroideryMotifComponent from './uploadEmbroideryMotifComponent';
import UploadMotifComponent from './uploadMotifComponent';

export default class FinishingProcessOverviewComponent extends HTMLElement {
    private finishingProcesses: NodeListOf<HTMLDivElement>;
    private config: ProductCustomizationConfigModel;
    private colorItems: NodeListOf<HTMLDivElement> | null = null;
    private readonly refreshOverviewUI: CustomizationPricingOverviewComponent;
    private veTechColors: {[key: string]: number[]};
    private positionId: number | null = null;
    private readonly FINISHING_ID_EMBROIDERY: number[] = [13, 41, 45];
    private finishingColors: FinishingColorsComponent | null = null;

    constructor() {
        super();

        this.veTechColors = {};
        this.finishingProcesses = this.querySelectorAll<HTMLDivElement>('.item');
        this.config = ProductCustomizationConfigModel.getInstance();
        this.refreshOverviewUI = notNull(document.querySelector<CustomizationPricingOverviewComponent>('#pricing-overview'));
    }

    connectedCallback() {
        this.positionId = parseInt(this.dataset.posid!, 10);
        this.colorItems = document.querySelectorAll<HTMLDivElement>(`.finishing-colors[data-posid="${this.positionId}"] .item`);
        this.setupVetechColors();
        this._attachFinishingProcessEventListeners();
        this.finishingColors = document.querySelector<FinishingColorsComponent>(`.finishing-colors[data-posid="${this.positionId}"]`);
    }

    disconnectedCallback() {
        this._detachFinishingProcessEventListeners();
    }

    private setupVetechColors() {
        const veTechColors: {[key: string]: number[]} = {};
        for (const item of this.finishingProcesses) {
            const colors = item.dataset.colors!.split(',').map((color) => parseInt(color, 10));
            veTechColors[item.dataset.vetech!] = colors;
        }
        this.veTechColors = veTechColors;
    }

    private handleProcessClick = (process: HTMLElement) => (event: Event) => {
        const activeItem = notNull(document.querySelector<HTMLDivElement>('.positions .active'));
        const posIdOfActiveElement = notNull(activeItem.getAttribute('data-posid'));
        const newSelection = (event.target as Element).closest('.item') as HTMLDivElement;
        const activeMountingPositionItem = notNull(
            document.querySelector<HTMLDivElement>(`clx-customization-mounting-position .positions .item[data-posid="${posIdOfActiveElement}"]`),
        );
        const finishingProcessTextOfActiveMountingPositionItem = notNull(
            activeMountingPositionItem.querySelector<HTMLDivElement>('.display-finishing-process'),
        );
        const finishingColorTextOfActiveMountingPositionItem = notNull(activeMountingPositionItem.querySelector<HTMLDivElement>('.display-finishing-color'));

        this._setCurrentFinishingProcess(newSelection);
        const finishingProcessName = notNull(newSelection.querySelector<HTMLDivElement>('.name-of-process'));
        finishingProcessTextOfActiveMountingPositionItem.innerText = finishingProcessName.innerText;

        finishingColorTextOfActiveMountingPositionItem.innerText = this.getActiveColorItemTextFromDOM();
    };

    public _attachFinishingProcessEventListeners(): void {
        const carouselItem = notNull(
            document.querySelector<HTMLDivElement>(`clx-customization-mounting-position .positions .item[data-posid="${this.positionId}"]`),
        );

        this.finishingProcesses.forEach((process) => {
            process.addEventListener('click', this.handleProcessClick(process));
            if (process.classList.contains('active')) {
                const finishingProcessName = notNull(process.querySelector<HTMLDivElement>('div'));
                notNull(carouselItem.querySelector<HTMLDivElement>('.display-finishing-process')).innerText = finishingProcessName.innerText;
                this.config.setVetechId(notNull(this.positionId), parseInt(process.dataset.vetech!));
                this.displayEmbroideryMotifUploadBanner(process);
            }
        });
    }

    public _detachFinishingProcessEventListeners(): void {
        this.finishingProcesses.forEach((process) => {
            process.removeEventListener('click', this.handleProcessClick(process));
        });
    }

    private _setCurrentFinishingProcess(newSelection: HTMLDivElement): void {
        if (newSelection.classList.contains('disabled')) {
            return;
        }

        const currentProcess: HTMLDivElement | null = this.querySelector('.active');

        if (currentProcess) {
            currentProcess.classList.remove('active');
        }

        this.config.setVetechId(notNull(this.positionId), parseInt(newSelection.dataset.vetech!));
        newSelection.classList.add('active');

        this.updateAvailableColors();
        this.restoreEmbroideryConfigData(newSelection);
        this.displayEmbroideryMotifUploadBanner(newSelection);
        this.updateCustomizationOverview(MicroConversionTrackingEventsEnum.customization_configuration_change, this.config);
    }

    private updateAvailableColors(): void {
        const currentVetechId = this.config.getVetechid(notNull(this.positionId));
        const availableColorsForCurrentVetech = this.veTechColors![currentVetechId];

        // Set coloritems, if it is empty
        if (this.colorItems!.length === 0) {
            this.colorItems = document.querySelectorAll<HTMLDivElement>('.finishing-colors .item');
        }

        this.colorItems!.forEach((item) => {
            item.classList.add('disabled');
            item.classList.remove('active');
            item.classList.remove('inactive');
        });

        availableColorsForCurrentVetech.forEach((colorOption) => {
            const matchingColorItem = document.querySelector<HTMLDivElement>(
                `.finishing-colors[data-posid="${this.positionId!}"] .item[data-noc="${colorOption}"]`,
            );
            if (matchingColorItem) {
                matchingColorItem.classList.remove('disabled');
            }
        });

        // select the first available color
        const firstAvailableColor = Array.from(this.colorItems!).find((item) => !item.classList.contains('disabled'));
        if (firstAvailableColor) {
            firstAvailableColor.classList.add('active');
            this.config.setAmountOfColors(notNull(this.positionId), parseInt(firstAvailableColor.dataset.noc!, 10));
        } else {
            console.error('No available colors found. Dead branch?');
            reportError(new Error('No available colors found. Dead branch?'));
            // this.config.setAmountOfColors(undefined);
        }
    }

    private async updateCustomizationOverview(eventName: string, config: ProductCustomizationConfigModel): Promise<void> {
        await this.refreshOverviewUI.refreshOverviewUIAndHandleErrors(config, eventName);
    }

    private getActiveColorItemTextFromDOM(): string {
        const possibleColorItems = Array.from(this.colorItems!);
        const activeColorItem = notNull(possibleColorItems.find((item) => item.classList.contains('active')));
        const colorItemText = notNull(activeColorItem.querySelector<HTMLDivElement>('.number-of-colors-text'));
        // we trim the text to remove any leading or trailing whitespace
        return colorItemText.innerText.trim();
    }

    private displayEmbroideryMotifUploadBanner(newSelection: HTMLDivElement) {
        const embroideryMotifComponent = notNull(this.querySelector<UploadEmbroideryMotifComponent>('clx-customization-upload-embroidery-motif'));
        const normalMotifComponent = notNull(this.querySelector<UploadMotifComponent>('clx-customization-upload-motif'));
        if (this.FINISHING_ID_EMBROIDERY.includes(parseInt(newSelection.getAttribute('data-vetech') ?? '0'))) {
            embroideryMotifComponent.style.display = 'block';
            normalMotifComponent.style.display = 'none';
        } else {
            embroideryMotifComponent.style.display = 'none';
            normalMotifComponent.style.display = 'block';
            this.finishingColors?.hideColorsSetAutomaticallyAlert();
        }
    }

    private restoreEmbroideryConfigData(newSelection: HTMLDivElement) {
        if (this.FINISHING_ID_EMBROIDERY.includes(parseInt(newSelection.getAttribute('data-vetech') ?? '0'))) {
            notNull(this.querySelector<UploadEmbroideryMotifComponent>('.upload-motif'));
            let emboideryConfigModel = ProductCustomizationStitchingConfigModel.getInstance();
            let positionConfig = emboideryConfigModel.getConfigForPosition(this.positionId!);
            if (positionConfig.numberOfColors && this.finishingColors instanceof FinishingColorsComponent) {
                this.finishingColors.setNumberOfMotifColorsFromTajimaApiResult(positionConfig.numberOfColors);
                this.config.setNumberOfStitches(this.positionId!, positionConfig.numberOfStitches);
            }
        }
    }
}
customElements.define('clx-customization-finishing-process-overview', FinishingProcessOverviewComponent);
